"use client";

import { useState, useEffect, useMemo } from "react";
import { Shield, DollarSign, AlertCircle, CheckCircle2, XCircle, AlertTriangle, Star, Loader2 } from "lucide-react";
import { Badge } from "./ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

// Types for underwriting tiers
type UnderwritingTier = 'PREFERRED' | 'STANDARD' | 'GRADED' | 'MODIFIED' | 'DECLINED' | 'UNKNOWN';

interface UnderwritingResult {
  carrier: string;
  tier: UnderwritingTier;
  reason?: string;
}

interface QuoteData {
  id: string;
  carrier: string;
  product: string;
  monthlyPremium: number;
  annualPremium: number;
  /** Face amount / coverage (raw dollars). Optional so externally-passed
   *  quotes (e.g. from useGetQuotesByLeadId) don't have to plumb it through. */
  faceAmount?: number;
  amBestRating: string;
  eligibility: string;
  isSuggested?: boolean;
  /** Carrier name was wrapped in `**` in the AI response — the AI was
   *  flagging it as the recommended option. Rendered with gold accent. */
  isHighlighted?: boolean;
}

interface CombinedResult {
  carrier: string;
  tier: UnderwritingTier;
  reason?: string;
  quote?: QuoteData;
  hasQuote: boolean;
}

interface AgentCarrier {
  carrierSlug: string;
  carrierName: string;
  isPreferred?: boolean;
}

interface UnderwritingQuoteTableProps {
  aiResponse: string;
  quotes?: QuoteData[];
  isLoadingQuotes?: boolean;
  showOnlyMatched?: boolean;
  agentCarriers?: AgentCarrier[]; // Agent's appointed carriers - used to filter results
  showSuggestionsIfRequested?: boolean; // Show non-appointed carriers if user asked for suggestions
}

// Parse AI response to extract underwriting results
function parseUnderwritingResponse(response: string): UnderwritingResult[] {
  const results: UnderwritingResult[] = [];

  if (!response) return results;

  // Patterns to match tier headers
  const tierPatterns: { pattern: RegExp; tier: UnderwritingTier }[] = [
    { pattern: /PREFERRED\s*(?:\/\s*LEVEL)?(?:\s*\(.*?\))?:/gi, tier: 'PREFERRED' },
    { pattern: /STANDARD\s*(?:\(.*?\))?:/gi, tier: 'STANDARD' },
    { pattern: /GRADED\s*(?:\(.*?\))?:/gi, tier: 'GRADED' },
    { pattern: /MODIFIED\s*(?:\(.*?\))?:/gi, tier: 'MODIFIED' },
    { pattern: /DECLINED?\s*(?:\(.*?\))?:/gi, tier: 'DECLINED' },
  ];

  // Split response into sections by tier headers
  let currentTier: UnderwritingTier = 'UNKNOWN';
  const lines = response.split('\n');

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Check if this line is a tier header
    let foundTier = false;
    for (const { pattern, tier } of tierPatterns) {
      if (pattern.test(trimmedLine)) {
        currentTier = tier;
        foundTier = true;
        break;
      }
      // Reset pattern lastIndex
      pattern.lastIndex = 0;
    }

    if (foundTier) continue;

    // Parse carrier entries (lines starting with - or *)
    const carrierMatch = trimmedLine.match(/^[-*•]\s*(.+?)(?::\s*(.+))?$/);
    if (carrierMatch && currentTier !== 'UNKNOWN') {
      const carrierName = carrierMatch[1].trim();
      const reason = carrierMatch[2]?.trim();

      // Skip if it looks like a header or instruction
      if (carrierName.length < 3 ||
          carrierName.toLowerCase().includes('don\'t see') ||
          carrierName.toLowerCase().includes('request')) {
        continue;
      }

      results.push({
        carrier: carrierName,
        tier: currentTier,
        reason,
      });
    }
  }

  return results;
}

// Parse AI response to extract quote results from markdown tables
function parseQuoteTableFromResponse(response: string): QuoteData[] {
  const results: QuoteData[] = [];

  if (!response) return results;

  // Find markdown tables in the response
  const lines = response.split('\n');
  let inTable = false;
  let headerIndices: { carrier: number; product: number; monthly: number; annual: number; face: number; rating: number } | null = null;

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Skip separator lines (|---|---|)
    if (trimmedLine.match(/^\|[\s-:|]+\|$/)) {
      continue;
    }

    // Check if this is a table row
    if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
      const cells = trimmedLine.split('|').map(c => c.trim()).filter(c => c !== '');

      // Check if this is a header row
      if (!headerIndices) {
        const headerRow = cells.map(c => c.toLowerCase());
        const carrierIdx = headerRow.findIndex(h => h.includes('carrier'));
        const productIdx = headerRow.findIndex(h => h.includes('product'));
        const monthlyIdx = headerRow.findIndex(h => h.includes('monthly') || h === 'mo');
        const annualIdx = headerRow.findIndex(h => h.includes('annual') || h === 'yr');
        const faceIdx = headerRow.findIndex(h => h.includes('face') || h.includes('coverage'));
        const ratingIdx = headerRow.findIndex(h => h.includes('rating') || h.includes('best'));

        if (carrierIdx !== -1 && monthlyIdx !== -1) {
          headerIndices = {
            carrier: carrierIdx,
            product: productIdx,
            monthly: monthlyIdx,
            annual: annualIdx,
            face: faceIdx,
            rating: ratingIdx,
          };
          inTable = true;
          continue;
        }
      }

      // Parse data row
      if (inTable && headerIndices && cells.length >= 3) {
        const carrierCell = cells[headerIndices.carrier] || '';
        // Detect AI's `**Carrier**` bold marker — it means "this is the
        // recommended carrier." We strip the markers but remember the
        // flag so we can render the row with a gold accent.
        const isHighlighted = /\*\*[^*]+\*\*/.test(carrierCell);
        // Strip emoji indicators + markdown bold markers in one pass.
        const carrierName = carrierCell
          .replace(/\*+/g, '')               // **SBLI** → SBLI
          .replace(/[⚠️★✅❌]/g, '')          // emoji indicators
          .trim();

        if (carrierName.length < 2) continue;

        // Parse price values (handle $45.00 or $45 or N/A)
        const parsePrice = (cell: string | undefined): number => {
          if (!cell) return 0;
          const match = cell.match(/\$?([\d,]+\.?\d*)/);
          return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
        };

        // Parse face amount (handle $10,000 or $10K or 10000)
        const parseFace = (cell: string | undefined): number => {
          if (!cell) return 0;
          const kMatch = cell.match(/\$?([\d,]+\.?\d*)\s*[kK]/);
          if (kMatch) return parseFloat(kMatch[1].replace(/,/g, '')) * 1000;
          const match = cell.match(/\$?([\d,]+\.?\d*)/);
          return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
        };

        const monthlyPremium = parsePrice(cells[headerIndices.monthly]);
        const annualPremium = headerIndices.annual !== -1 ? parsePrice(cells[headerIndices.annual]) : monthlyPremium * 12;
        const faceAmount = headerIndices.face !== -1 ? parseFace(cells[headerIndices.face]) : 0;

        results.push({
          id: `parsed-${results.length}-${carrierName.substring(0, 10)}`,
          carrier: carrierName,
          product: (headerIndices.product !== -1 ? (cells[headerIndices.product] || '') : '').replace(/\*+/g, '').trim(),
          monthlyPremium,
          annualPremium,
          faceAmount,
          amBestRating: headerIndices.rating !== -1 ? (cells[headerIndices.rating] || '') : '',
          eligibility: '',
          isSuggested: carrierCell.includes('⚠️'),
          isHighlighted,
        });
      }
    } else if (inTable && trimmedLine !== '') {
      // Non-table line after being in table - end of table
      inTable = false;
      headerIndices = null;
    }
  }

  return results;
}

// Match carriers between underwriting and quotes (fuzzy matching)
function matchCarriers(underwriting: UnderwritingResult[], quotes: QuoteData[]): CombinedResult[] {
  const results: CombinedResult[] = [];
  const matchedQuoteIds = new Set<string>();

  // Normalize carrier name for comparison
  const normalizeCarrierName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/\s+(company|co|corp|corporation|inc|llc|ltd|insurance|life|assurance)\.?/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Check if two carrier names match
  const carriersMatch = (name1: string, name2: string): boolean => {
    const norm1 = normalizeCarrierName(name1);
    const norm2 = normalizeCarrierName(name2);

    // Exact match
    if (norm1 === norm2) return true;

    // One contains the other (for variations like "Foresters" vs "Foresters Financial")
    if (norm1.includes(norm2) || norm2.includes(norm1)) return true;

    // First word match (for company identifiers)
    const words1 = norm1.split(' ');
    const words2 = norm2.split(' ');
    if (words1[0] === words2[0] && words1[0].length > 3) return true;

    return false;
  };

  // Process underwriting results
  for (const uw of underwriting) {
    const matchingQuote = quotes.find(q =>
      !matchedQuoteIds.has(q.id) && carriersMatch(uw.carrier, q.carrier)
    );

    if (matchingQuote) {
      matchedQuoteIds.add(matchingQuote.id);
    }

    results.push({
      carrier: uw.carrier,
      tier: uw.tier,
      reason: uw.reason,
      quote: matchingQuote,
      hasQuote: !!matchingQuote,
    });
  }

  // Add quotes that weren't matched to underwriting results
  for (const quote of quotes) {
    if (!matchedQuoteIds.has(quote.id)) {
      results.push({
        carrier: quote.carrier,
        tier: 'UNKNOWN',
        quote,
        hasQuote: true,
      });
    }
  }

  return results;
}

// Get tier badge styling
function getTierBadge(tier: UnderwritingTier) {
  switch (tier) {
    case 'PREFERRED':
      return { className: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: Star };
    case 'STANDARD':
      return { className: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2 };
    case 'GRADED':
      return { className: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertTriangle };
    case 'MODIFIED':
      return { className: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertCircle };
    case 'DECLINED':
      return { className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle };
    default:
      return { className: 'bg-gray-100 text-gray-600 border-gray-200', icon: AlertCircle };
  }
}

// Get eligibility styling
function getEligibilityStyle(eligibility: string) {
  switch (eligibility?.toLowerCase()) {
    case 'eligible':
      return 'text-green-600';
    case 'conditionally eligible':
      return 'text-amber-600';
    case 'not eligible':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

// Helper to check if AI response indicates user asked for suggestions
// The AI will include "⚠️ NOT APPOINTED" marker when showing non-appointed carriers
function aiShowedSuggestions(response: string): boolean {
  const suggestionIndicators = [
    /⚠️\s*NOT APPOINTED/i,
    /not in your appointments/i,
    /would need to.*contract/i,
    /you.*not.*appointed/i,
    /outside your.*carriers/i,
    /additional.*carriers.*consider/i,
  ];
  return suggestionIndicators.some(p => p.test(response));
}

// Helper to normalize carrier name for matching
function normalizeCarrierForMatch(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+(company|co|corp|corporation|inc|llc|ltd|insurance|life|assurance|group|of|the)\.?/gi, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
}

// Check if a carrier name matches any in the agent's list
function carrierMatchesAgentList(carrierName: string, agentCarriers: AgentCarrier[]): boolean {
  const normalizedName = normalizeCarrierForMatch(carrierName);

  return agentCarriers.some(ac => {
    const normalizedAgentName = normalizeCarrierForMatch(ac.carrierName);
    const normalizedSlug = normalizeCarrierForMatch(ac.carrierSlug);

    // Check if names match (allowing partial matches for common abbreviations)
    return normalizedName === normalizedAgentName ||
           normalizedName === normalizedSlug ||
           normalizedAgentName.includes(normalizedName) ||
           normalizedName.includes(normalizedAgentName) ||
           normalizedSlug.includes(normalizedName) ||
           normalizedName.includes(normalizedSlug);
  });
}

export function UnderwritingQuoteTable({
  aiResponse,
  quotes = [],
  isLoadingQuotes = false,
  showOnlyMatched = false,
  agentCarriers = [],
  showSuggestionsIfRequested = true,
}: UnderwritingQuoteTableProps) {
  // Parse underwriting results from AI response
  const underwritingResults = useMemo(() => {
    return parseUnderwritingResponse(aiResponse);
  }, [aiResponse]);

  // Parse quotes from AI response table (for InsuraBot quotes without lead context)
  const parsedQuotesFromResponse = useMemo(() => {
    return parseQuoteTableFromResponse(aiResponse);
  }, [aiResponse]);

  // Merge external quotes with parsed quotes (external quotes take priority)
  const allQuotes = useMemo(() => {
    if (quotes.length > 0) {
      // If we have external quotes (from lead context), use those
      return quotes;
    }
    // Otherwise, use quotes parsed from AI response
    return parsedQuotesFromResponse;
  }, [quotes, parsedQuotesFromResponse]);

  // Check if AI showed suggestions (indicates user asked for them)
  // The AI will only include "⚠️ NOT APPOINTED" markers if user explicitly asked for suggestions
  const showSuggestions = useMemo(() => {
    return showSuggestionsIfRequested && aiShowedSuggestions(aiResponse);
  }, [aiResponse, showSuggestionsIfRequested]);

  // Combine underwriting with quotes
  const combinedResults = useMemo(() => {
    let results = matchCarriers(underwritingResults, allQuotes);

    // Filter to only show agent's carriers (unless user asked for suggestions)
    if (agentCarriers.length > 0) {
      results = results.map(result => {
        const isAgentCarrier = carrierMatchesAgentList(result.carrier, agentCarriers);
        return {
          ...result,
          isSuggested: !isAgentCarrier, // Mark non-agent carriers as suggested
        };
      }).filter(result => {
        // Keep agent's carriers
        if (!result.isSuggested) return true;
        // Only keep suggested carriers if user asked for suggestions
        return showSuggestions;
      });
    }

    // Sort by tier priority, then by whether they have quotes
    const tierOrder: Record<UnderwritingTier, number> = {
      'PREFERRED': 1,
      'STANDARD': 2,
      'GRADED': 3,
      'MODIFIED': 4,
      'DECLINED': 5,
      'UNKNOWN': 6,
    };

    return results.sort((a, b) => {
      // Agent's carriers first, suggested carriers last
      if ((a as any).isSuggested && !(b as any).isSuggested) return 1;
      if (!(a as any).isSuggested && (b as any).isSuggested) return -1;

      // First by tier
      const tierDiff = tierOrder[a.tier] - tierOrder[b.tier];
      if (tierDiff !== 0) return tierDiff;

      // Then by having quote (those with quotes first)
      if (a.hasQuote && !b.hasQuote) return -1;
      if (!a.hasQuote && b.hasQuote) return 1;

      // Then by premium (lowest first)
      if (a.quote && b.quote) {
        return a.quote.monthlyPremium - b.quote.monthlyPremium;
      }

      return 0;
    });
  }, [underwritingResults, allQuotes, agentCarriers, showSuggestions]);

  // Filter if requested
  const displayResults = showOnlyMatched
    ? combinedResults.filter(r => r.hasQuote)
    : combinedResults;

  // If no underwriting results AND no quotes, don't show the table
  if (underwritingResults.length === 0 && allQuotes.length === 0) {
    return null;
  }

  return (
    <Card className="mt-4 border-2 border-blue-100">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {underwritingResults.length > 0 ? (
            <Shield className="h-5 w-5 text-blue-600" />
          ) : (
            <DollarSign className="h-5 w-5 text-green-600" />
          )}
          {underwritingResults.length > 0 && allQuotes.length > 0
            ? 'Underwriting & Quote Summary'
            : allQuotes.length > 0
              ? 'Quote Comparison'
              : 'Underwriting Summary'}
        </CardTitle>
        <CardDescription className="text-xs">
          {allQuotes.length > 0 && underwritingResults.length > 0
            ? 'Combined view of carrier eligibility and premium quotes'
            : allQuotes.length > 0
              ? 'Quote comparison from your appointed carriers'
              : 'Carrier eligibility based on underwriting analysis'}
          {isLoadingQuotes && (
            <span className="ml-2 inline-flex items-center">
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
              Loading quotes...
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border overflow-hidden overflow-x-auto">
          <Table className="min-w-0">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="min-w-[120px]">Carrier</TableHead>
                <TableHead className="min-w-[120px]">Product</TableHead>
                {/* Only show Tier column if we have underwriting results */}
                {underwritingResults.length > 0 && (
                  <TableHead className="w-[90px] whitespace-nowrap">Tier</TableHead>
                )}
                <TableHead className="w-[80px] whitespace-nowrap text-right">Monthly</TableHead>
                <TableHead className="w-[80px] whitespace-nowrap text-right hidden sm:table-cell">Annual</TableHead>
                {/* Coverage replaces Rating. Rating was empty for every row
                    (the source markdown table doesn't include A.M. Best
                    ratings), so the column was just a wall of dashes.
                    Coverage / face amount IS in the source and is the more
                    useful column for a quote comparison anyway. */}
                <TableHead className="w-[90px] whitespace-nowrap text-right hidden sm:table-cell">Coverage</TableHead>
                {underwritingResults.length > 0 && (
                  <TableHead className="hidden md:table-cell">Notes</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayResults.map((result, index) => {
                const tierBadge = getTierBadge(result.tier);
                const TierIcon = tierBadge.icon;
                const isNotAppointed = (result as any).isSuggested;

                const isHighlighted = result.quote?.isHighlighted === true;
                return (
                  <TableRow
                    key={`${result.carrier}-${index}`}
                    // Highlighted rows (AI marked carrier with **) get a
                    // soft gold background. Subtler than amber/blue so it
                    // reads as "recommended" not "warning".
                    className={`${result.hasQuote ? '' : 'bg-gray-50/50'} ${isNotAppointed ? 'bg-amber-50/30' : ''} ${isHighlighted ? 'bg-yellow-50/60' : ''}`}
                  >
                    <TableCell className="font-medium align-top">
                      <div className="flex flex-wrap items-start gap-1.5">
                        <span
                          className={`break-words leading-tight ${isHighlighted ? 'text-yellow-700 font-bold' : ''}`}
                          title={result.carrier}
                        >
                          {result.carrier}
                        </span>
                        {isHighlighted && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-yellow-100 text-yellow-800 rounded flex-shrink-0 whitespace-nowrap font-semibold">
                            ★ Recommended
                          </span>
                        )}
                        {isNotAppointed && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded flex-shrink-0 whitespace-nowrap">
                            Not Appointed
                          </span>
                        )}
                        {!isNotAppointed && !isHighlighted && result.quote?.isSuggested && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded flex-shrink-0 whitespace-nowrap">
                            Suggested
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      {result.quote?.product ? (
                        <span
                          className="text-sm text-gray-700 break-words leading-tight block"
                          title={result.quote.product}
                        >
                          {result.quote.product.trim()}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </TableCell>
                    {/* Only show Tier column if we have underwriting results */}
                    {underwritingResults.length > 0 && (
                      <TableCell>
                        {result.tier !== 'UNKNOWN' ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className={`${tierBadge.className} flex items-center gap-1 text-xs`}>
                                  <TierIcon className="h-3 w-3" />
                                  {result.tier}
                                </Badge>
                              </TooltipTrigger>
                              {result.reason && (
                                <TooltipContent>
                                  <p className="text-xs max-w-[200px]">{result.reason}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {result.quote ? (
                        <span className="font-semibold text-green-600 whitespace-nowrap tabular-nums text-right block">
                          ${result.quote.monthlyPremium.toFixed(2)}
                        </span>
                      ) : isLoadingQuotes ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell align-top">
                      {result.quote ? (
                        <span className="text-green-600 whitespace-nowrap tabular-nums text-right block">
                          ${result.quote.annualPremium.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </TableCell>
                    {/* Coverage / face amount. Replaces the old Rating
                        column which was empty for every row. */}
                    <TableCell className="hidden sm:table-cell align-top">
                      {result.quote?.faceAmount && result.quote.faceAmount > 0 ? (
                        <span className="font-medium whitespace-nowrap tabular-nums text-right block text-gray-900">
                          ${result.quote.faceAmount.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </TableCell>
                    {/* Only show Notes column if we have underwriting results */}
                    {underwritingResults.length > 0 && (
                      <TableCell className="hidden md:table-cell align-top">
                        <div className="text-xs text-gray-600">
                          {result.reason && (
                            <span className="block">{result.reason}</span>
                          )}
                          {result.quote?.eligibility && (
                            <span className={`block ${getEligibilityStyle(result.quote.eligibility)}`}>
                              {result.quote.eligibility}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}

              {displayResults.length === 0 && (
                <TableRow>
                  <TableCell colSpan={underwritingResults.length > 0 ? 7 : 5} className="text-center py-8 text-gray-500">
                    No underwriting or quote data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Legend - only show when there are underwriting results */}
        {underwritingResults.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                <Star className="h-2.5 w-2.5 mr-0.5" />
                PREFERRED
              </Badge>
              <span className="text-gray-500">Best rates, immediate coverage</span>
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]">
                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                STANDARD
              </Badge>
              <span className="text-gray-500">Normal rates</span>
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                GRADED
              </Badge>
              <span className="text-gray-500">2-3 year wait</span>
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200 text-[10px]">
                <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                MODIFIED
              </Badge>
              <span className="text-gray-500">Higher rates</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default UnderwritingQuoteTable;
